// ResetPasswordPage.jsx
import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingPassword, setPendingPassword] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);
  const [tokenValid, setTokenValid] = useState(null);

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  // Validate token
  useEffect(() => {
    if (!token) return setTokenValid(false);
    api.get(`/api/reset-password/validate/?token=${encodeURIComponent(token)}`)
      .then(res => setTokenValid(res.data.valid))
      .catch(() => setTokenValid(false));
  }, [token]);

  if (tokenValid === false) return <div>Reset link is invalid or expired.</div>;
  if (tokenValid === null) return <div>Checking link...</div>;

  // Handle initial password submission
  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    try {
      // Try password reset. If 2FA is required, the backend will respond accordingly.
      const res = await api.post("/api/reset-password/", { token, new_password: password });
      if (res.data["2fa_required"]) {
        // Show 2FA code prompt, don't update password yet
        setShow2FA(true);
        setPendingToken(token);
        setPendingPassword(password);
        setPendingUserId(res.data.user_id);
        setError(""); // Clear errors
      } else if (res.data.token) {
        // Auto-login: save JWT, user_id, redirect
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user_id", res.data.user_id);
        setSuccess("Password updated! Redirecting...");
        setTimeout(() => navigate("/frameworks"), 1200);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Reset failed. Try again.");
    }
  };

  // Handle 2FA code submission
  const handle2FA = async (e) => {
    e.preventDefault();
    setError("");
    if (!twoFACode || twoFACode.length < 6) {
      setError("Enter your 6-digit authentication code.");
      return;
    }
    try {
      // Submit password reset with 2FA code
      const res = await api.post("/api/reset-password/", {
        token: pendingToken,
        new_password: pendingPassword,
        two_fa_code: twoFACode,
      });
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user_id", res.data.user_id);
        setSuccess("Password updated! Redirecting...");
        setTimeout(() => navigate("/frameworks"), 1200);
      } else if (res.data["2fa_required"]) {
        setError("Invalid or expired code. Try again.");
      } else {
        setError("Reset failed. Try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Reset failed. Try again.");
    }
  };

  // Show 2FA prompt if needed
  if (show2FA) {
    return (
      <div>
        <h2>Two-Factor Authentication</h2>
        <div>Enter the 6-digit code from your authenticator app to continue.</div>
        {error && <div style={{ color: "red" }}>{error}</div>}
        <form onSubmit={handle2FA}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            placeholder="123456"
            maxLength={6}
            value={twoFACode}
            onChange={e => setTwoFACode(e.target.value)}
            autoFocus
            required
            style={{ letterSpacing: 4, fontSize: 18, width: 120, textAlign: "center" }}
          />
          <button type="submit" style={{ marginLeft: 10 }}>Verify</button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <h2>Reset Your Password</h2>
      {error && <div style={{ color: "red" }}>{error}</div>}
      {success && <div style={{ color: "green" }}>{success}</div>}
      <form onSubmit={handleReset}>
        <input
          type="password"
          placeholder="New password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          minLength={8}
          required
        />
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          minLength={8}
          required
        />
        <button type="submit">Reset Password</button>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
